import { Button, DatePicker, Input, Popover, Tabs, TimePicker } from 'antd';
import { FormattedMessage, FormattedTime, useIntl } from 'react-intl';
import { Controller } from 'react-hook-form';

import Icon from '../Icon/Icon';
import Popup from '../Popup/Popup';

import useScheduleEvent from './useScheduleEvent';

import { Props, Participant } from './ScheduleEvent.model';

import styles from './ScheduleEvent.module.scss';

const ScheduleEvent = ({ eventId, onClose, userId }: Props) => {
	const {
		addParticipant,
		control,
		errors,
		handleSubmit,
		isAuthorized,
		onCreateEvent,
		onUpdateEvent,
		removeParticipant,
		state: {
			events,
			network,
			participants,
		},
		watchStartTime,
	} = useScheduleEvent({ eventId, onClose, userId });
	const intl = useIntl();
	const { TextArea } = Input;
	const { TabPane } = Tabs;
	const renderParticipant = (onClick: (item: Participant) => void, participant: Participant, index: number) => (
		<div className={styles.participant} key={`mentor${index}`} onClick={onClick.bind(null, participant)}>
			{participant.imageId && !participant.isDeleted ?
				<div className={styles.avatar}><img alt="Profile" src={participant.imageId} /></div> :
				<div className={styles.circle}><Icon name="user" width="12px" color="text" /></div>}
			{!participant.isDeleted ? `${participant.firstName} ${participant.lastName}` : intl.formatMessage({ id: "userLabels.deletedUser" })}
		</div>
	);

	const filterNetworkList = (item: Participant, role: string): Participant | undefined => {
		let participantsArray = participants.map(participant => participant.userId);
		if (item.role === role && !participantsArray.includes(item.userId)) {
			return item;
		}
		return;
	}

	return (
		<Popup
			actions={
				<>
					<Button onClick={onClose}><FormattedMessage id="buttons.cancel" /></Button>
					{eventId ? <Button type="primary" onClick={handleSubmit((data) => onUpdateEvent(data))}><FormattedMessage id="buttons.updateEvent" /></Button>
						: <Button type="primary" onClick={handleSubmit((data) => onCreateEvent(data))}><FormattedMessage id="buttons.scheduleCall" /></Button>
					}
				</>
			}
			title={{ target: 'scheduleCall' }}
			onClose={onClose}
		>
			<div className={styles.wrapper}>
				<div className={styles.container}>
					<form>

						<div className={`${styles.fieldWrapper}`}>
							<label className={styles.floatingLabel}>
								<FormattedMessage id="formLabels.title" />
							</label>
							<Controller
								name="title"
								control={control}
								render={({ field }) => (
									<Input
										{...field}
										className={errors['title'] && 'error'}
										placeholder={intl.formatMessage({ id: `formPlaceholders.title` })}
									/>
								)}
								rules={{ required: true }}
							/>
						</div>

						<div className={`${styles.fieldWrapper}`}>
							<label className={styles.floatingLabel}>
								<FormattedMessage id="formLabels.description" />
							</label>
							<Controller
								name="description"
								control={control}
								render={({ field }) => (
									<TextArea
										{...field}
										className={errors['description'] && 'error'}
										placeholder={intl.formatMessage({ id: `formPlaceholders.description` })}
										rows={6}
									/>
								)} />
						</div>

						<div className={`${styles.fieldWrapper}`}>
							<label className={styles.floatingLabel}>
								<FormattedMessage id="formLabels.chooseDate" />
							</label>
							<Controller
								name="date"
								control={control}
								render={({ field }) => (
									<DatePicker
										{...field}
										className={errors['date'] && 'error'}
										format="DD MMM YYYY"
										suffixIcon={false}
										style={{ padding: 0, width: '100%' }}
									/>
								)}
								rules={{ required: true }}
							/>
						</div>

						<div className={styles.timePicker}>
							<div className={`${styles.fieldWrapper}`}>
								<label className={styles.floatingLabel}>
									<FormattedMessage id="formLabels.time" />
								</label>
								<Controller
									name="startTime"
									control={control}
									render={({ field }) => (
										<TimePicker
											{...field}
											className={errors['startTime'] && 'error'}
											format="HH:mm"
											placeholder={intl.formatMessage({ id: "formPlaceholders.time" })}
											suffixIcon={false}
											style={{ padding: 0 }}
										/>
									)}
									rules={{ required: true }}
								/>
							</div>
							<Icon
								className={styles.icon}
								color="black"
								name="horizontal-rule"
								type="light"
								width="48px"
							/>
							<div className={`${styles.fieldWrapper}`}>
								<Controller
									name="endTime"
									control={control}
									rules={{ min: watchStartTime }}
									render={({ field }) => (
										<TimePicker
											{...field}
											className={errors['endTime'] && 'error'}
											format="HH:mm"
											placeholder={intl.formatMessage({ id: "formPlaceholders.time" })}
											suffixIcon={false}
											style={{ padding: 0 }}
										/>
									)}
								/>
							</div>

							<div className={`${styles.fieldWrapper} ${styles.duration}`}>
								<Controller
									name="duration"
									control={control}
									render={({ field }) => (
										<Input {...field} readOnly />
									)}
								/>
							</div>
						</div>

						<div className={styles.participantsContainer}>
							<h3 className="accent"><FormattedMessage id="formLabels.participants" /></h3>
							<Popover
								className={styles.popover}
								content={
									<Tabs
										centered
										className={styles.popoverTab}
										defaultActiveKey="1"
										size="small"
									>
										<TabPane tab={intl.formatMessage({id: "role.Mentors"})} key="1">
											{network.filter(item => filterNetworkList(item, 'Mentor')).map(renderParticipant.bind(null, addParticipant))}
										</TabPane>
										<TabPane tab={intl.formatMessage({id: "role.Mentees"})} key="2">
											{network.filter(item => filterNetworkList(item, "Mentee")).map(renderParticipant.bind(null, addParticipant))}
										</TabPane>
										{
											isAuthorized(['ViewApplicantMentees']) &&
											<TabPane tab={intl.formatMessage({id: "role.ApplicantMentees"})} key="3">
												{network.filter(item => filterNetworkList(item, "ApplicantMentee")).map(renderParticipant.bind(null, addParticipant))}
											</TabPane>
										}
									</Tabs>}
								placement="rightTop"
								trigger="click"
							>
								<div className={styles.participant}><span className={styles.circle}><Icon name="plus" color="black" /></span><FormattedMessage id="buttons.addParticipants" /></div>
							</Popover>
							{participants.map(renderParticipant.bind(null, removeParticipant))}
						</div>
					</form>
				</div>

				<div className={styles.container}>
					<h3 className="accent"><FormattedMessage id="formLabels.schedules" /></h3>
					{events && events.length ? events.map((meeting) => (
						<div className={styles.scheduledMeeting}>
							<h4>{meeting.title}</h4>
							<span className="light"><FormattedTime value={new Date(meeting.startTime)} /> - <FormattedTime value={meeting.endTime} /></span>
						</div>
					)) : <span className="light"><FormattedMessage id="emptyContent.scheduledMeetings" /></span>}
				</div>
			</div>
		</Popup>
	);
}

export default ScheduleEvent;